import React from 'react';
import { Create, useForm } from '@refinedev/antd';
import { useNavigation } from '@refinedev/core';
import { Form, Input, InputNumber, Select, Switch, Space, Button, message } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { WorkoutPlanType } from '@wellverse/shared-ts';

export const WorkoutPlanCreate: React.FC = () => {
  const { formProps, onFinish } = useForm({ resource: 'workout-plans' });
  const { push } = useNavigation();

  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      if (values.schedule && typeof values.schedule === 'string') {
        try {
          values.schedule = JSON.parse(values.schedule);
        } catch {
          // leave as-is if not valid JSON
        }
      }
      await onFinish(values);
      message.success('Workout plan created');
      push('/workout-plans');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create workout plan';
      message.error(msg);
    }
  };

  return (
    <Create resource="workout-plans" saveButtonProps={{ onClick: () => formProps.form?.submit() }}>
      <Form {...formProps} layout="vertical" onFinish={(v) => { void handleSubmit(v); }}>
        <Form.Item label="Name" name="name" rules={[{ required: true }]}>
          <Input placeholder="e.g. 7-Day Strength Plan" />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea rows={4} placeholder="Describe the workout plan..." />
        </Form.Item>
        <Form.Item label="Plan Type" name="planType" initialValue={WorkoutPlanType.CUSTOM}>
          <Select placeholder="Select plan type">
            {Object.values(WorkoutPlanType).map((t) => (
              <Select.Option key={t} value={t}>{t}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Public" name="isPublic" valuePropName="checked" initialValue={false}>
          <Switch />
        </Form.Item>
        <Form.Item
          label="Rest between exercises"
          name="restBetweenExercises"
          valuePropName="checked"
          initialValue={true}
          tooltip="Show a full-screen rest after each completed exercise. Turn off for HYROX-style continuous plans."
        >
          <Switch />
        </Form.Item>
        <Form.Item label="Schedule (JSON)" name="schedule">
          <Input.TextArea rows={4} placeholder='{"monday": "chest", "tuesday": "back"}' />
        </Form.Item>
        <Form.Item label="Exercises">
          <Form.List name="exercises">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} direction="vertical" style={{ display: 'flex', marginBottom: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 8 }}>
                    <Space style={{ display: 'flex' }} align="baseline">
                      <Form.Item {...restField} name={[name, 'exerciseId']} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                        <Input placeholder="Exercise ID" style={{ width: 200 }} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'orderIndex']} style={{ marginBottom: 0 }}>
                        <InputNumber placeholder="Order" style={{ width: 80 }} min={0} />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                    <Space style={{ display: 'flex' }} align="baseline">
                      <Form.Item {...restField} name={[name, 'targetSets']} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                        <InputNumber placeholder="Sets" style={{ width: 80 }} min={1} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'targetReps']} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                        <InputNumber placeholder="Reps" style={{ width: 80 }} min={1} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'targetWeightKg']} style={{ marginBottom: 0 }}>
                        <InputNumber placeholder="Weight (kg)" style={{ width: 100 }} min={0} step={0.5} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'restSeconds']} style={{ marginBottom: 0 }}>
                        <InputNumber placeholder="Rest (sec)" style={{ width: 100 }} min={0} />
                      </Form.Item>
                    </Space>
                    <Form.Item {...restField} name={[name, 'notes']} style={{ marginBottom: 0 }}>
                      <Input placeholder="Notes" style={{ width: 400 }} />
                    </Form.Item>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} style={{ width: 400 }}>
                  Add exercise
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Create>
  );
};
